import React, { useState } from 'react';
import Icon from './Icon';
import Button from './Button';
import StripeService from '../services/stripeService';

const PaywallModal = ({ isOpen, onClose, credits = 0 }) => {
  const [loading, setLoading] = useState(null); // Track which plan is being processed
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const pricingPlans = [
    {
      id: 'prova',
      name: 'Prova på',
      price: '49 kr',
      credits: 50,
      features: [
        'Engångsbetalning',
        '50 chattar',
        'Riskmonitor',
        'Krypterad chat', 
        'Terapeutmatchning',
        'Personlig AI-terapeut 24/7'
      ],
    },
    {
      id: 'start',
      name: 'Komma igång', 
      price: '99 kr',
      credits: 150,
      features: [
        'Engångsbetalning',
        '150 chattar',
        'Riskmonitor',
        'Krypterad chat',
        'Terapeutmatchning',
        'Prioriterat stöd'
      ],
    },
    {
      id: 'power',
      name: 'Daglig användning',
      price: '349 kr',
      credits: 1000,
      features: [
        'Engångsbetalning',
        '1000 chattar',
        'Riskmonitor',
        'Krypterad chat',
        'Terapeutmatchning',
        'Alla premium-funktioner',
        'Direktkontakt med terapeuter',
        'Personlig vårdplan'
      ],
    }
  ];

  const handleSelectPlan = async (planId, planName) => {
    try {
      setLoading(planId);
      setError(null);
      
      // Get token from localStorage
      const token = localStorage.getItem('therapy_auth_token'); // Fixed: Use correct token key
      console.log('🔐 PaywallModal token check:', {
        tokenExists: !!token,
        tokenLength: token ? token.length : 0,
        tokenStart: token ? token.substring(0, 20) + '...' : 'null'
      });
      
      if (!token) {
        throw new Error('Du måste logga in för att köpa credits');
      }

      // Check if token is expired (simple check)
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Math.floor(Date.now() / 1000);
        if (payload.exp < currentTime) {
          localStorage.removeItem('therapy_auth_token'); // Fixed: Use correct token key
          throw new Error('Din session har gått ut. Vänligen logga in igen.');
        }
      } catch (tokenError) {
        console.error('Token validation error:', tokenError);
        localStorage.removeItem('therapy_auth_token'); // Fixed: Use correct token key
        throw new Error('Din session har gått ut. Vänligen logga in igen.');
      }

      // Create checkout session and redirect to Stripe
      // Save selected plan for later use
      localStorage.setItem('selectedPlanType', planId);
      
      await StripeService.createCheckoutSession(planId, token);
      
    } catch (error) {
      console.error('Error selecting plan:', error);
      setError(error.message);
      setLoading(null);
      
      // If token expired, redirect to login after showing error
      if (error.message.includes('session har gått ut')) {
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000); // Show error message for 2 seconds before redirect
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-pink-600 text-white p-6 rounded-t-lg">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold mb-2">
                {credits === 0 ? 'Inga credits kvar' : 'Fylla på credits'}
              </h2>
              <p className="text-red-100">
                {credits === 0 
                  ? 'Gör en engångsbetalning för att fylla på dina credits. '
                  : `Du har ${credits} credits kvar. Fylla på för att fortsätta utan avbrott.`
                }
              </p>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="text-white hover:text-gray-300 transition-colors"
              >
                <Icon name="x" size={24} />
              </button>
            )}
          </div>
        </div>

        {/* Pricing Plans */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className="relative rounded-lg border-2 border-gray-200 bg-white p-6 flex flex-col hover:shadow-lg transition-shadow"
              >
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-gray-900">{plan.price}</span>
                    <span className="text-gray-600 text-sm"></span>
                  </div>
                  <div className="flex items-center justify-center space-x-2 mb-4">
                    <Icon name="credit-card" size={16} className="text-red-500" />
                    <span className="text-lg font-semibold text-red-600">
                      {plan.credits} credits
                    </span>
                  </div>
                </div>

                <ul className="space-y-2 mb-6 flex-grow">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start space-x-2">
                      <Icon name="check" size={14} className="text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  variant="primary"
                  size="full"
                  onClick={() => handleSelectPlan(plan.id, plan.name)}
                  disabled={loading === plan.id}
                  className="w-full mt-auto bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white border-none disabled:opacity-50"
                >
                  {loading === plan.id ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Laddar...</span>
                    </div>
                  ) : (
                    credits === 0 ? 'Köp credits' : 'Köp credits'
                  )}
                </Button>
              </div>
            ))}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <Icon name="alert-circle" size={16} className="text-red-500" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 text-center">
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <Icon name="shield" size={20} className="text-green-500" />
                <span className="font-semibold text-gray-800">100% säker betalning</span>
              </div>
              <p className="text-sm text-gray-600">
                Krypterad chat • GDPR-kompatibel • Banknivå säkerhet
              </p>
            </div>
            
            <p className="text-xs text-gray-500">
              Credits återställs automatiskt varje månad. Inga bindningstider.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaywallModal;
